import { Box, Text, Button, Flex, Spinner, Link } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SubscriptionSelector } from "./components/SubscriptionSelector";
import { PaymentTitle } from "./components/PaymentTitle";
import { PaymentValueProps } from "./components/PaymentValueProps";
import { PaymentEyebrow } from "./components/PaymentEyebrow";
import { CloseIcon } from "@chakra-ui/icons";
import { t } from "i18next";
import { useAccountData } from "../../hooks/useAccountData";
import { ReaderLockedPages } from "./components/LockedPages";
import {
  GET_SUBSCRIPTION_PRICING_QUERY_KEY,
  getSubscriptionPricing,
  createWebSubscriptionFromSavedPaymentMethod,
  createWebSubscriptionFromSetupSession,
  hasManageableWebSubscription,
  isLegacySubscriptionPageFlow,
  isBundlePaymentSetupPayload,
  isLegacyWebSubscription,
  isStripeBillingManageableSubscription,
  isReleasePaymentSetupPayload,
  isWebSubscriptionCheckoutPayload,
  redirectToLegacyWebSubscriptions,
} from "./api/paymentApi";
import { StickyPurchaseButton } from "./componentsV2/StickyPurchaseButton";
import { PromoCodeInput } from "./components/PromoCodeInput";
import { FunnelComponentProps, FunnelContextArea } from "../funnel/types";
import axios from "axios";
import { Release, ServerResponse } from "../../types";
import { useUserData } from "../../hooks/useUserData";
import { FUNNEL_TYPES } from "../funnel/utils/contants";
import { SubscriptionPriceTypes } from "./types";
import {
  useIsSmallBreakpoint,
  useMobileBreakpoint,
} from "../../utils/useBreakpoints";
import { EventTypes } from "../../screens/root/api/eventsApi";
import { useIsVisible } from "../../hooks/useIsVisible";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIsSmallWindowHeight } from "../../utils/useIsSmallWindowHeight";
import { AuthFunnel } from "../auth/components/AuthFunnel";
import { pendingAuthPaymentConfigStore } from "../../hookstate/pendingAuthPaymentConfigStore";
import { CaretLeftLargeIcon } from "../icons/CaretLeftLargeIcon";
import { rootComponentsStore } from "../../hookstate/rootComponentsStore";
import { FunnelSteps } from "../funnel/FunnelSteps";
import { useStripeCheckoutSubscriptionSetup } from "./hooks/useStripeCheckoutSubscriptionSetup";
import { useStripeCustomerPortal } from "./hooks/useStripeCustomerPortal";
import {
  useStripeCheckoutReturn,
  useStripeCheckoutReturnPollingTimeoutToast,
} from "./hooks/useStripeCheckoutReturn";
import { useRefetchQueries } from "../../services/axiosInstance";
import { ACCOUNT_INIT_QUERY_KEY } from "../../appStore";
import { GET_ACCOUNT_ME_QUERY_KEY } from "../auth/api/authApi";
import {
  SubscriptionSetupPayment,
  SUBSCRIPTION_SETUP_PAYMENT_FORM_ID,
} from "./components/SubscriptionSetupPayment";
import {
  clearPendingGuestStripeSubscriptionSetup,
  getPendingGuestStripeSubscriptionSetup,
  markPendingGuestStripeSubscriptionSetupIntentCompleted,
  updatePendingGuestStripeSubscriptionSetupPayload,
} from "./utils/pendingGuestStripeSubscriptionSetup";

export function SubscriptionModalHeader({
  release,
  modalContext,
  setFunnelType,
  onClose,
}: FunnelComponentProps) {
  const { userData } = useUserData();
  const { ppb_gold_price_point, premium_only } = release ?? {};

  const isGoldUser = userData?.gold ?? false;

  const isContextWithNoX = modalContext === FunnelContextArea.verticalReader;
  const isContextWithPrePaywall =
    modalContext === FunnelContextArea.reader ||
    modalContext === FunnelContextArea.verticalReader;

  const hasBackButton =
    isContextWithPrePaywall &&
    !isGoldUser &&
    (!premium_only || ppb_gold_price_point);

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      flexDirection="row"
      width="100%"
      padding="24px 24px"
    >
      <Box>
        {hasBackButton && (
          <Button
            variant="iconOnly"
            onClick={() => setFunnelType(FUNNEL_TYPES.prePaywall)}
            minWidth={0}
          >
            <CaretLeftLargeIcon color="dune.400" boxSize="20px" />
          </Button>
        )}
      </Box>
      {isContextWithNoX ? null : (
        <Button
          gc-testing-id="btnCloseSubscriptionModal"
          variant="iconOnly"
          onClick={onClose}
          minWidth={0}
        >
          <CloseIcon color="dune.100" />
        </Button>
      )}
    </Flex>
  );
}

export const SubscriptionModalBody = memo(
  ({
    modalContext = FunnelContextArea.default,
    onSuccess: onFunnelSuccess,
    onClose,
    sendEvent,
    hideValueProps = false,
    hidePromoCode = false,
    showEmailPasswordForm = false,
    centerWelcomeText = false,
    largeLoginCTA = false,
    shouldShowSteps,
    stepCount,
    currentStep,
    stepText,
    stepDisplayText,
    showStepIndicators,
    updateFunnelConfig,
    displayExploreMoreContent,
    initialPromoCode,
    customHeader,
    customSubmitButtonText,
    showYearlyOnly = false,
    hideHeader = false,
    removeBackground = false,
    fullWidth = false,
    isInlineEmbed = false,
  }: FunnelComponentProps & {
    context?: string;
    customHeader?: string | JSX.Element;
    customSubmitButtonText?: string;
    displayExploreMoreContent?: boolean;
    hideValueProps?: boolean;
    hidePromoCode?: boolean;
    release?: Release;
    centerWelcomeText?: boolean;
    showEmailPasswordForm?: boolean;
    largeLoginCTA?: boolean;
    initialPromoCode?: string;
    showYearlyOnly?: boolean;
    hideHeader?: boolean;
    removeBackground?: boolean;
    fullWidth?: boolean;
    /** When true, suppresses outer padding and disables fixed-footer portal (for CMS inline embeds) */
    isInlineEmbed?: boolean;
  }) => {
    const accountData = useAccountData();
    const { userData } = useUserData();
    const [urlSearchParams] = useSearchParams();
    const [promoCode, setPromoCode] = useState<string>(
      initialPromoCode ?? urlSearchParams.get("code") ?? "",
    );
    const [appliedPromoCode, setAppliedPromoCode] = useState<string>(
      initialPromoCode ?? urlSearchParams.get("code") ?? "",
    );
    const [activeType, setActiveType] = useState<"yearly" | "monthly">();
    const [activeAuthType, setActiveAuthType] = useState<FUNNEL_TYPES>();
    const isSmallBreakpoint = useIsSmallBreakpoint();
    const isMobile = useMobileBreakpoint();
    const isSmallWindowHeight = useIsSmallWindowHeight();

    const [yearlySub, setYearlySub] = useState<SubscriptionPriceTypes>();
    const [monthlySub, setMonthlySub] = useState<SubscriptionPriceTypes>();
    const [pendingLegacyRedirectAfterAuth, setPendingLegacyRedirectAfterAuth] =
      useState(false);
    const navigate = useNavigate();
    const handledPendingGuestSetupRef = useRef(false);

    const isReaderContext =
      modalContext === FunnelContextArea.reader ||
      modalContext === FunnelContextArea.verticalReader;
    const isGoldContext = modalContext === FunnelContextArea.gold;
    const showSmallContent =
      isSmallBreakpoint || isGoldContext || isSmallWindowHeight;
    const goldLarge = isGoldContext && !isSmallBreakpoint;
    const selectedQueryKey = [
      ...GET_SUBSCRIPTION_PRICING_QUERY_KEY,
      appliedPromoCode,
    ];
    const [checkoutError, setCheckoutError] = useState<string>();
    const checkoutWaiting = false;
    const {
      startSetupCheckout,
      waiting: setupCheckoutWaiting,
      error: setupCheckoutError,
      setError: setSetupCheckoutError,
      setupIntent: guestSetupIntent,
      clearSetupIntent: clearGuestSetupIntent,
    } = useStripeCheckoutSubscriptionSetup();
    const { openPortal, waiting: portalWaiting, error: portalError, setError: setPortalError } =
      useStripeCustomerPortal();
    const refetchStripeReturnAccount = useRefetchQueries(
      ACCOUNT_INIT_QUERY_KEY,
      GET_ACCOUNT_ME_QUERY_KEY,
    );
    const onPollingTimeout = useStripeCheckoutReturnPollingTimeoutToast();
    const [checkoutValidationError, setCheckoutValidationError] = useState<string>();

    const getSubscriptionCheckoutPayload = useCallback(() => {
      const { access_token_type_id, promo_code } =
        (activeType === "yearly" ? yearlySub : monthlySub) ?? {};

      return {
        accessTokenTypeId: access_token_type_id ?? 0,
        incentiveCode: promoCode.length ? promoCode : promo_code,
        userOfferUuid:
          typeof accountData?.user_offer === "string"
            ? accountData.user_offer
            : undefined,
        context: "subscription_modal",
      };
    }, [accountData?.user_offer, activeType, yearlySub, monthlySub, promoCode]);
    const subscriptionCheckoutPayload = useMemo(
      () => getSubscriptionCheckoutPayload(),
      [getSubscriptionCheckoutPayload],
    );
    const subscriptionCheckoutPayloadKey = JSON.stringify(
      subscriptionCheckoutPayload,
    );
    const guestSetupIntentPayloadKey = guestSetupIntent
      ? JSON.stringify(guestSetupIntent.payload)
      : undefined;
    const hasCompletedPendingGuestSetup =
      !!getPendingGuestStripeSubscriptionSetup()?.completedSetupIntentId;

    function onError(error: Error) {
      setActiveAuthType(undefined);
      let errorMessage = t(
        "components.payments.subscription.subscriptionDeclined",
      );

      if (axios.isAxiosError<ServerResponse<string>>(error)) {
        errorMessage = error.response?.data?.payload.results || errorMessage;
      }
      return errorMessage;
    }

    const { data } = useQuery({
      enabled: !!accountData,
      queryKey: selectedQueryKey,
      queryFn: () => getSubscriptionPricing(appliedPromoCode),
      refetchOnWindowFocus: false,
    });

    const submitHostedCheckout = useCallback(async () => {
      if (!userData) {
        try {
          await startSetupCheckout(subscriptionCheckoutPayload);
        } catch (checkoutError) {
          const errorMessage = onError(checkoutError as Error);
          setCheckoutError(errorMessage);
          setSetupCheckoutError(errorMessage);
        }
        return;
      }

      try {
        const checkoutPayload = getSubscriptionCheckoutPayload();
        if (
          data?.data?.payload?.results?.provider?.has_saved_payment_method ===
          true
        ) {
          try {
            await createWebSubscriptionFromSavedPaymentMethod(checkoutPayload);
            onFunnelSuccess?.({
              data: [null, null],
              options: {
                didPurchaseSubscription: true,
                purchasedYearly: activeType === "yearly",
              },
            });
            return;
          } catch {
            // Saved Stripe Billing payment methods can require SCA or become stale.
            // Fall through to embedded Elements so the reader can update/confirm.
          }
        }

        await startSetupCheckout(checkoutPayload);
      } catch (checkoutError) {
        const errorMessage = onError(checkoutError as Error);
        setCheckoutError(errorMessage);
      }
    }, [
      activeType,
      data?.data?.payload?.results?.provider?.has_saved_payment_method,
      getSubscriptionCheckoutPayload,
      onFunnelSuccess,
      onError,
      setCheckoutError,
      setSetupCheckoutError,
      startSetupCheckout,
      subscriptionCheckoutPayload,
      userData,
    ]);

    useEffect(() => {
      const results = data?.data.payload.results;
      const subscriptions = results?.subscriptions;

      if (!subscriptions) return;

      setYearlySub(
        subscriptions.find((sub) => sub.billing_frequency === "yearly"),
      );
      setMonthlySub(
        subscriptions.find((sub) => sub.billing_frequency === "monthly"),
      );
    }, [data]);

    useEffect(() => {
      if (!activeType || !appliedPromoCode) {
        if (yearlySub?.promo_code) {
          setActiveType("yearly");
        } else if (monthlySub?.promo_code) {
          setActiveType("monthly");
        } else {
          setActiveType("yearly");
        }
      } else {
        if (appliedPromoCode === yearlySub?.promo_code) {
          setActiveType("yearly");
        } else if (appliedPromoCode == monthlySub?.promo_code) {
          setActiveType("monthly");
        }
      }
    }, [yearlySub, monthlySub]);

    const ref = useRef<HTMLDivElement>(null);
    const isVisible = useIsVisible(ref, 0.5);
    const alreadyTracked = useRef(false);

    useEffect(() => {
      if (sendEvent && isVisible && !alreadyTracked.current) {
        sendEvent(EventTypes.viewed_subscription_offer);
        alreadyTracked.current = true;
      }
    }, [isVisible]);

    // Default to using "modal" subscription context unless otherwise defined
    const contextMap = {
      [FunnelContextArea.reader]: "reader",
      [FunnelContextArea.verticalReader]: "reader",
    };
    const getActivePaywall = () => {
      let activePaywalls =
        activeType === yearlySub?.billing_frequency
          ? yearlySub?.paywalls
          : monthlySub?.paywalls;

      if (modalContext === FunnelContextArea.offerPopUp) {
        activePaywalls = activePaywalls?.filter((paywall) => paywall.is_intro);
      }

      const activeContext =
        contextMap[modalContext as keyof typeof contextMap] ?? "modal";
      return activePaywalls?.find(
        (paywall) => paywall.context === activeContext,
      );
    };

    const [isPromoValid, setIsPromoValid] = useState(true);
    useEffect(() => {
      if ((!promoCode && !appliedPromoCode) || appliedPromoCode === promoCode) {
        setIsPromoValid(true);
        setCheckoutValidationError(undefined);
        return;
      }
      const timeout = setTimeout(() => {
        setIsPromoValid(false);
        setCheckoutValidationError(
          t("components.payments.subscription.applyPromoToComplete"),
        );
      }, 400);
      return () => clearTimeout(timeout);
    }, [promoCode, appliedPromoCode]);

    // Determine if we need to use the pending payment config
    // If the user is gold we don't need to use the pending config
    // If the user is not gold we send the pending config to the api
    // and purchase.
    useEffect(() => {
      const pendingConfig = pendingAuthPaymentConfigStore.get({
        noproxy: true,
      });
      if (!pendingConfig) {
        return;
      }

      if (
        isReleasePaymentSetupPayload(pendingConfig) ||
        isBundlePaymentSetupPayload(pendingConfig)
      ) {
        return;
      }

      if (!userData) {
        return;
      }

      if (userData.gold) {
        // Auth recovery can land on an already-Gold account; do not open a second checkout.
        pendingAuthPaymentConfigStore.set(undefined);
        onFunnelSuccess?.({
          data: [null, null],
          options: { didPurchaseSubscription: false },
        });
        return;
      }

      const resumeCheckout = isWebSubscriptionCheckoutPayload(pendingConfig)
        ? data?.data?.payload?.results?.provider?.has_saved_payment_method
          ? createWebSubscriptionFromSavedPaymentMethod(pendingConfig)
          : startSetupCheckout(pendingConfig)
        : null;
      if (resumeCheckout === null) {
        return;
      }
      void resumeCheckout.catch((checkoutError) => {
        const errorMessage = onError(checkoutError as Error);
        setCheckoutError(errorMessage);
      });
      pendingAuthPaymentConfigStore.set(undefined);
    }, [
      onError,
      setCheckoutError,
      startSetupCheckout,
      data?.data?.payload?.results?.provider?.has_saved_payment_method,
      userData,
    ]);

    // Clear the pending config on unmount
    useEffect(() => {
      return () => {
        pendingAuthPaymentConfigStore.set(undefined);
      };
    }, []);

    const activePaywall = getActivePaywall();
    const provider = data?.data?.payload?.results?.provider;
    const hasManagedSubscription = hasManageableWebSubscription(provider);
    const usesLegacySubscriptionPageFlow = isLegacySubscriptionPageFlow(provider);
    const hasStripeBillingManagedSubscription =
      isStripeBillingManageableSubscription(provider);
    const hasLegacyManagedSubscription = isLegacyWebSubscription(provider);
    const hasSavedStripeBillingPaymentMethod =
      provider?.has_saved_payment_method === true;
    const paymentWaiting =
      checkoutWaiting || setupCheckoutWaiting || portalWaiting;
    const responseError =
      portalError ??
      setupCheckoutError ??
      checkoutError ??
      checkoutValidationError;
    const isGuestSetupPaymentVisible =
      !hasManagedSubscription && !!guestSetupIntent;
    const subscriptionButtonText = hasManagedSubscription
      ? t("components.payments.subscription.manageSubscription")
      : customSubmitButtonText ||
        (userData
          ? t("components.payments.subscription.confirmYourPayment")
          : t("components.payments.subscription.subscribeToGlobalComixGold"));

    useEffect(() => {
      if (!userData || handledPendingGuestSetupRef.current) {
        return;
      }

      const pendingGuestSetup = getPendingGuestStripeSubscriptionSetup();
      if (!pendingGuestSetup?.completedSetupIntentId) {
        return;
      }

      handledPendingGuestSetupRef.current = true;

      if (userData.gold) {
        clearPendingGuestStripeSubscriptionSetup();
        onFunnelSuccess?.({
          data: [null, null],
          options: { didPurchaseSubscription: false },
        });
        return;
      }

      void createWebSubscriptionFromSetupSession({
        ...pendingGuestSetup.payload,
        setupIntentId: pendingGuestSetup.completedSetupIntentId,
        clientNonce: pendingGuestSetup.clientNonce,
      })
        .then(() => {
          clearPendingGuestStripeSubscriptionSetup();
          onFunnelSuccess?.({
            data: [null, null],
            options: {
              didPurchaseSubscription: true,
              purchasedYearly:
                pendingGuestSetup.payload.accessTokenTypeId ===
                yearlySub?.access_token_type_id,
            },
          });
        })
        .catch((setupError) => {
          handledPendingGuestSetupRef.current = false;
          setCheckoutError(onError(setupError as Error));
        });
    }, [onFunnelSuccess, userData, yearlySub?.access_token_type_id]);

    useEffect(() => {
      if (
        activeAuthType ||
        hasCompletedPendingGuestSetup ||
        usesLegacySubscriptionPageFlow ||
        hasManagedSubscription ||
        hasStripeBillingManagedSubscription ||
        hasLegacyManagedSubscription ||
        hasSavedStripeBillingPaymentMethod ||
        setupCheckoutWaiting ||
        !isPromoValid ||
        subscriptionCheckoutPayload.accessTokenTypeId <= 0 ||
        guestSetupIntentPayloadKey === subscriptionCheckoutPayloadKey
      ) {
        return;
      }

      void startSetupCheckout(subscriptionCheckoutPayload).catch((checkoutError) => {
        const errorMessage = onError(checkoutError as Error);
        setCheckoutError(errorMessage);
        setSetupCheckoutError(errorMessage);
      });
    }, [
      activeAuthType,
      guestSetupIntentPayloadKey,
      hasCompletedPendingGuestSetup,
      hasLegacyManagedSubscription,
      hasManagedSubscription,
      hasSavedStripeBillingPaymentMethod,
      hasStripeBillingManagedSubscription,
      isPromoValid,
      setupCheckoutWaiting,
      startSetupCheckout,
      subscriptionCheckoutPayload,
      subscriptionCheckoutPayloadKey,
      usesLegacySubscriptionPageFlow,
    ]);

    useStripeCheckoutReturn({
      expectedFlow: "portal",
      onSuccessReturn: async () => {
        // Portal returns can legitimately leave subscription fields unchanged
        // (for example after a payment-method update), so a single account refresh
        // is the completion signal instead of waiting for a field delta.
        await refetchStripeReturnAccount();
      },
      onPollingTimeout,
    });

    useEffect(() => {
      if (!pendingLegacyRedirectAfterAuth || !userData?.edit_url) {
        return;
      }

      setPendingLegacyRedirectAfterAuth(false);
      pendingAuthPaymentConfigStore.set(undefined);
      redirectToLegacyWebSubscriptions(userData.edit_url);
    }, [pendingLegacyRedirectAfterAuth, userData?.edit_url]);

    if (!data || !yearlySub || !monthlySub || !activePaywall) {
      return (
        <Flex
          width="100%"
          justifyContent="center"
          alignItems="center"
          height="531px"
          padding={isMobile ? "16px" : "24px"}
          ref={ref}
        >
          <Spinner color="blaze.blaze" />
        </Flex>
      );
    }

    return (
      <Flex
        flexDirection={goldLarge ? "row" : "column"}
        gap={showSmallContent ? "16px" : "24px"}
        alignItems="center"
        justifyContent={goldLarge ? "space-evenly" : "center"}
        borderRadius={isInlineEmbed ? undefined : "16px"}
        padding={isInlineEmbed ? "0" : isMobile ? "16px" : "24px"}
        ref={ref}
      >
        {shouldShowSteps ? (
          <FunnelSteps
            stepCount={stepCount}
            currentStep={currentStep}
            stepText={stepText}
            text={stepDisplayText}
            hideProgressIndicators={!showStepIndicators}
          />
        ) : (activeAuthType && !isGoldContext) ||
        hideHeader ? null : (
          <Flex
            flexDirection="column"
            width="fit-content"
            gap={isGoldContext ? "32px" : "24px"}
            alignItems={goldLarge ? "flex-start" : "center"}
            paddingBottom={isGoldContext ? "24px" : undefined}
          >
            {isReaderContext && <ReaderLockedPages />}
            {activePaywall?.eyebrow && (
              <PaymentEyebrow
                alt={isGoldContext}
                text={activePaywall.eyebrow}
              />
            )}
            <PaymentTitle
              size={goldLarge ? "40px" : undefined}
              textAlign={goldLarge ? "left" : "center"}
              text={
                customHeader ||
                (activeAuthType && !isGoldContext
                  ? ""
                  : (activePaywall?.main_title ?? ""))
              }
            />
            {!hideValueProps && (
              <PaymentValueProps
                showSeparators={false}
                alignItems="start"
                valueProps={activePaywall?.value_props ?? ["", "", ""]}
                iconColor={isGoldContext ? "dune.dune" : undefined}
              />
            )}
          </Flex>
        )}
        <Flex
          flexDirection="column"
          maxWidth={fullWidth ? "none" : goldLarge ? "500px" : "100%"}
          width="100%"
          gap={isMobile || isGoldContext ? "16px" : "24px"}
          padding={
            removeBackground ? undefined : isGoldContext ? "24px" : undefined
          }
          alignItems={isGoldContext ? "flex-start" : "center"}
          background={
            removeBackground
              ? "transparent"
              : isGoldContext
                ? "linear-gradient(346deg, rgba(20, 21, 23, 0.75) 66.94%, rgba(68, 90, 119, 0.11) 200.5%);"
                : undefined
          }
          borderColor={
            removeBackground
              ? undefined
              : isGoldContext
                ? "1px solid var(--White-Transparency-White-10, rgba(255, 255, 255, 0.10));"
                : undefined
          }
          backdropFilter={
            removeBackground
              ? undefined
              : isGoldContext
                ? "blur(18px)"
                : undefined
          }
          boxShadow={
            removeBackground
              ? undefined
              : isGoldContext
                ? "0px 4px 24px 0px rgba(0, 0, 0, 0.40)"
                : undefined
          }
          borderRadius={
            removeBackground ? undefined : isGoldContext ? "16px" : undefined
          }
        >
          {!activeAuthType ? (
            <>
              <Flex
                flexDirection="column"
                width="100%"
                gap="16px"
                paddingTop="14px"
              >
                <SubscriptionSelector
                  onClick={setActiveType}
                  subscriptionPrice={yearlySub}
                  active={yearlySub?.billing_frequency === activeType}
                />
                {!showYearlyOnly && (
                  <SubscriptionSelector
                    onClick={setActiveType}
                    subscriptionPrice={monthlySub}
                    active={monthlySub?.billing_frequency === activeType}
                  />
                )}
                <Text
                  color="neutral.300"
                  fontSize="12px"
                  lineHeight="150%"
                  textAlign="center"
                >
                  {t(
                    "components.payments.subscription.cancelAnytimeAdditionalPurchase",
                  )}
                </Text>
              </Flex>
              {hidePromoCode ? null : (
                <PromoCodeInput
                  promoApplied={appliedPromoCode}
                  isValid={
                    appliedPromoCode === yearlySub?.promo_code ||
                    appliedPromoCode === monthlySub?.promo_code
                  }
                  applyPromoCode={setAppliedPromoCode}
                  onChange={setPromoCode}
                />
              )}
              {isGuestSetupPaymentVisible ? (
                    <SubscriptionSetupPayment
                      clientSecret={guestSetupIntent.client_secret}
                      setupIntentId={guestSetupIntent.setup_intent_id}
                      clientNonce={guestSetupIntent.client_nonce}
                      submitButtonText={subscriptionButtonText}
                      disabled={paymentWaiting || (!isPromoValid && !hasManagedSubscription)}
                      hideInternalSubmitButton
                      onComplete={(setupIntentId, clientNonce) => {
                        updatePendingGuestStripeSubscriptionSetupPayload(
                          getSubscriptionCheckoutPayload(),
                        );
                        markPendingGuestStripeSubscriptionSetupIntentCompleted(
                          setupIntentId,
                        );
                        clearGuestSetupIntent();
                        if (userData) {
                          void createWebSubscriptionFromSetupSession({
                            ...getSubscriptionCheckoutPayload(),
                            setupIntentId,
                            clientNonce,
                          })
                            .then(() => {
                              clearPendingGuestStripeSubscriptionSetup();
                              onFunnelSuccess?.({
                                data: [null, null],
                                options: {
                                  didPurchaseSubscription: true,
                                  purchasedYearly: activeType === "yearly",
                                },
                              });
                            })
                            .catch((setupError) => {
                              setCheckoutError(onError(setupError as Error));
                            });
                          return;
                        }

                        setActiveAuthType(FUNNEL_TYPES.signup);
                      }}
                    />
                  ) : null}
              <StickyPurchaseButton
                isDisabled={paymentWaiting || (!isPromoValid && !hasManagedSubscription)}
                loading={paymentWaiting}
                text={subscriptionButtonText}
                formId={SUBSCRIPTION_SETUP_PAYMENT_FORM_ID}
                onClick={
                  isGuestSetupPaymentVisible
                    ? undefined
                    : () => {
                        setPortalError(undefined);
                        setCheckoutError(undefined);
                        if (usesLegacySubscriptionPageFlow) {
                          if (userData?.edit_url) {
                            redirectToLegacyWebSubscriptions(userData.edit_url);
                          } else {
                            pendingAuthPaymentConfigStore.set(undefined);
                            setPendingLegacyRedirectAfterAuth(true);
                            setActiveAuthType(FUNNEL_TYPES.signup);
                          }
                          return;
                        }

                        if (hasStripeBillingManagedSubscription) {
                          void openPortal("manage").catch((portalOpenError) => {
                            setPortalError(onError(portalOpenError as Error));
                          });
                          return;
                        }

                        if (hasLegacyManagedSubscription && userData?.edit_url) {
                          redirectToLegacyWebSubscriptions(userData.edit_url);
                          return;
                        }

                        void submitHostedCheckout();
                      }
                }
              />
              {responseError ? (
                <Text
                  color="error.300"
                  fontSize="14px"
                  lineHeight="130%"
                  width="100%"
                >
                  {responseError}
                </Text>
              ) : null}
              {displayExploreMoreContent && (
                <Link
                  gc-testing-id="btnExploreMoreContent"
                  fontSize="14px"
                  lineHeight="130%"
                  color="neutral.300"
                  fontWeight="500"
                  textDecorationLine="underline"
                  alignSelf="center"
                  onClick={() => {
                    navigate("/");
                    onClose?.();
                    rootComponentsStore.openSlideInMenu.set(undefined);
                    rootComponentsStore.slideInMenuConfig.set({});
                  }}
                >
                  {t("components.payments.subscription.exploreMoreContent")}
                </Link>
              )}
            </>
          ) : (
            <AuthFunnel
              activeAuthType={activeAuthType}
              setFunnelType={setActiveAuthType}
              updateFunnelConfig={updateFunnelConfig}
              onClose={onClose}
              onSuccess={() => undefined}
              waiting={paymentWaiting}
              sendEvent={sendEvent}
              centerWelcomeText={centerWelcomeText}
              showEmailPasswordForm={showEmailPasswordForm}
              largeLoginCTA={largeLoginCTA}
            />
          )}
        </Flex>
      </Flex>
    );
  },
);
